import {
	Component,
	OnInit,
	ComponentFactoryResolver,
	OnDestroy,
	AfterViewInit,
	ViewChild,
	ElementRef,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngxs/store';
import { Subject, Observable } from 'rxjs';
import { first, takeUntil, take, tap } from 'rxjs/operators';
// States
import { InboxStateModel } from '../../../inbox/state/model/inbox-state.model';
import { RulesState } from '../../rules/state/rules.state';
import { DeleteSelectedRule } from '../../rules/state/action/rules.actions';
// Models
import { DOCUMENT_RULES_UI_SECTIONS, UISTATE } from '../../model/quality.constant';
import { RuleModel } from '../../rules/model/rule.model';
import { ReviewSectionsPages } from './in-review-sections-pages.model';
// Components
import { SubContentService, BroadcastEventModel, BroadcastService } from '@cv-invision/core';
import { RulesComponent } from '../../rules/rules.component';
import { HISTORICAL_VALUE_EVENT } from '../../historical-values/historical-values.component';
import { QualityState } from '../../state/quality.state';
import { RuleEngine } from '../../rules/model/rule-engine';
import { UI_FIELD_SELECTED_EVENT } from '../../rules/rules.component';

@Component({
	selector: 'cv-in-review-sections',
	templateUrl: './in-review-sections.component.html',
	styleUrls: ['./in-review-sections.component.scss']
})
export class InReviewSectionsComponent implements OnInit, AfterViewInit, OnDestroy {
	isRecalled$: Observable<boolean> = this.store.select(QualityState.getIsbeingRecalled) ;
	inboxStateModel$: Observable<InboxStateModel> = this.store.select(state => state.intelligence.inbox);

	@ViewChild('contentRevised', { static: true }) contentRevised: ElementRef<any>;
	selectedRule$: Observable<any> = this.store.select(RulesState.getSelectedRule);
	private atComponentDestroy = new Subject();
	public selectedOrderRow = null;

	targets = {
		data: [],
		primary: []
	};
	selectedTargets = [];
	selectedType = '';
	sections: any;

	documentOrder: any;
	documentSchema: any;
	overviewUiState: any;
	uiState: string;

	selectedSection = '';
	selectedRules: DOCUMENT_RULES_UI_SECTIONS;

	scrollT: number;
	schemaNumber: any;
	isLayoutLoaded = false;
	isOrderRecalled = false;
	isOrderRecallCompleted = false;

	constructor(
		private activeRouter: ActivatedRoute,
		private store: Store,
		private subContentService: SubContentService,
		private componentFactoryResolver: ComponentFactoryResolver,
		private broadcastService: BroadcastService
	) {
		this.overviewUiState = UISTATE;
		this.store
			.select(s => s.quality)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(quality => {
				if (quality && quality.documentOrder && quality.documentSchema) {
					if (!this.isOrderRecalled && quality.isRecalled) {
						this.isOrderRecalled = true;
					}
					this.documentSchema = quality.documentSchema;
					this.documentOrder = quality.documentOrder;
					this.uiState = UISTATE.IS_READY;

					const schemaName = quality.documentSchema.Metadata[0].DisplayText;
					switch (schemaName) {
						case 'CV Review 1004':
							this.schemaNumber = 1004;
							break;
						case 'CV Review 1073':
							this.schemaNumber = 1073;
							break;
						case 'CV Review 1075':
							this.schemaNumber = 1075;
							break;
						case 'CV Review 2055':
							this.schemaNumber = 2055;
							break;
						case 'Inspection Property V3':
							this.schemaNumber = 'cvds';
							break;
						case 'CV Review BPO Interior':
							this.schemaNumber = 'BPOInterior';
							break;
					}

					if (!this.isLayoutLoaded || this.isOrderRecallCompleted) {
						if (this.isOrderRecallCompleted) {
							setTimeout(() => this.loadLayout(), 2500);
							this.isOrderRecallCompleted = false;
						} else {
							this.loadLayout();
						}
					}
				}
			});

		this.inboxStateModel$
			.pipe(
				take(1),
				tap((inboxStateModel: InboxStateModel) => {
					if (inboxStateModel && inboxStateModel.selectedOrderRow) {
						this.selectedOrderRow = inboxStateModel.selectedOrderRow;
					}
				})
			)
			.subscribe();
	}

	/**
	 * Load the 1004  Rules through store managment and then open the Rules SubContent panel.
	 *
	 */
	private loadRules(): void {
		this.subContentService
			.open({
				componentFactoryResolver: this.componentFactoryResolver,
				component: RulesComponent,
				data: {
					uiSection: this.selectedRules,
					targets: this.targets
				}
			})
			.subscribe(updateEvent => {
				this.getSelectedRule(updateEvent);
			});
	}

	/**
	 * Load layout only once, once we know the schema of the order.
	 *
	 */
	async loadLayout(): Promise<void> {
		this.isLayoutLoaded = true;
		const url = await this.activeRouter.url.pipe(first()).toPromise();
		this.selectedSection = url[0].path;
		const schemasPages = new ReviewSectionsPages();
		const layoutCreator = schemasPages.pages[this.schemaNumber][this.selectedSection].layout;
		if (layoutCreator) {
			this.sections = new layoutCreator().sections;
			this.selectedRules = schemasPages.pages[this.schemaNumber][this.selectedSection].rules;
		}

		if (DOCUMENT_RULES_UI_SECTIONS[this.selectedSection.toUpperCase()]) {
			RuleEngine.setSelectedSection(this.store, DOCUMENT_RULES_UI_SECTIONS[this.selectedSection.toUpperCase()]);
		}
	}

	/**
	 * get the rule selected
	 * @param item selected rule model
	 */
	getSelectedRule(item: RuleModel): void {
		this.selectedTargets = [];
		this.selectedTargets = item.targets;
		this.selectedType = item.messageType;
	}

	/**
	 * get scroll event
	 * @param $event
	 */
	onScroll(event: any): void {
		const target = event.target as Element;
		const isHistoricalCmp = target.classList.contains('cv-historical-values__container');

		if (!isHistoricalCmp) {
			const { scrollTop } = event.target;
			this.scrollT = scrollTop;
			this.broadcastService.broadcast({
				name: HISTORICAL_VALUE_EVENT,
				event: {
					isOpen: false,
					data: [],
					pos: {}
				}
			});
		}
	}

	ngOnInit(): void {
		this.onFieldSelectedListener();
		this.isRecalled$.pipe(takeUntil(this.atComponentDestroy)).subscribe(isBeingRecalled => {
			if (!isBeingRecalled && this.isOrderRecalled) {
				this.isOrderRecallCompleted = true;
				this.isOrderRecalled = false;
			}
		});
	}

	ngAfterViewInit(): void {
		const el = this.contentRevised.nativeElement;
		// why this instead of hotlistener, scroll event dont work so well with that
		if (el) {
			el.addEventListener('scroll', this.onScroll.bind(this), true);
		}
		// After a rule has been selected if it belongs to the same section it will try to Highlight the Field
		this.selectedRule$.pipe(takeUntil(this.atComponentDestroy)).subscribe((selectedRule: any) => {
			if (selectedRule && RuleEngine.isCurrentUISection(selectedRule, this.selectedRules)) {
				setTimeout(() => {
					this.getSelectedRule(selectedRule);
					// We Clean latest value
					this.store.dispatch(new DeleteSelectedRule());
				});
			}
		});
	}

	/**
	 * Listen for fields selection
	 */
	private onFieldSelectedListener(): void {
		this.broadcastService.on(UI_FIELD_SELECTED_EVENT, (broadcast: BroadcastEventModel) => {
			const rule = broadcast.event.rule;
			this.getSelectedRule(rule);
		});
	}

	/**
	 * Close all when the element is destroyed
	 * in case a Sub Component remains visible
	 */
	ngOnDestroy(): void {
		this.schemaNumber = null;
		// Globally Destroy anything attached to it
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}
}
